import _ from 'lodash'
import dsUtils from '../utils/utils'
import appStudioDataModel from './appStudioDataModel'
import type {
    BlocksDashboard,
    BlocksDashboardData,
    BlocksDashboardDescriptor,
    DashboardPointer,
    Callback,
    Callback1,
    PS,
    CompRef,
    Pointer
} from '@wix/document-services-types'
import page from '../page/page'
import dashboardConfig from './dashboardConfigs/dashboardConfig'
import component from '../component/component'
import features from '../features/features'
import {dashboardInitialCode} from './dashboardConfigs/initialDashboardCode'
import fileSystemAPI from '../wixCode/services/fileSystemAPI'
import {getShallowAppStudioData} from './getAppStudioData'
import {getDataPointerById, getShallowDataById} from './blocksDataModel'
import type {DataModelExtensionAPI} from '@wix/document-manager-extensions'
import nameGenerator from './nameGenerator'
import pageData from '../page/pageData'

const DASHBOARD_TYPE = 'DashboardDescriptor'
const DASHBOARD_CONTROLLER_TYPE = 'dashboard-controller'
const DASHBOARD_TITLE_DEFAULT = 'Dashboard page'

function getDashboardInfo(ps: PS, dashboardId: string): BlocksDashboard | undefined {
    const dashboardData: BlocksDashboardData = getShallowDataById(ps, dashboardId)

    if (dashboardData) {
        return {
            id: dashboardData.id,
            rootCompId: dashboardData.rootCompId,
            pointer: getDataPointerById(ps, dashboardId),
            routePath: dashboardData.routePath,
            title: dashboardData.title ?? DASHBOARD_TITLE_DEFAULT,
            activeSidebarPageId: dashboardData.activeSidebarPageId,
            hideInSidebar: dashboardData.hideInSidebar,
            isRoutePathConnectedToTitle: dashboardData.isRoutePathConnectedToTitle ?? true
        }
    }
}

function getAllDashboards(ps: PS): BlocksDashboard[] {
    const dashboardIds = getShallowAppStudioData(ps).dashboards ?? []

    return dashboardIds.map(dashboardId => getDashboardInfo(ps, dashboardId))
}

function createBlankDashboardData(
    ps: PS,
    dashboardPointer: DashboardPointer,
    pageRef: CompRef,
    descriptor: BlocksDashboardDescriptor
): BlocksDashboardData {
    const {dataModel} = ps.extensionAPI as DataModelExtensionAPI
    const dashboardData = dataModel.createDataItemByType<BlocksDashboardData>(DASHBOARD_TYPE)
    dashboardData.id = dashboardPointer.id
    dashboardData.rootCompId = `#${pageRef.id}`
    dashboardData.routePath = descriptor.routePath
    dashboardData.title = descriptor.title
    dashboardData.activeSidebarPageId = descriptor?.activeSidebarPageId ?? null
    dashboardData.hideInSidebar = descriptor?.hideInSidebar ?? false

    return dashboardData
}

function addDashboardToAppStudioModel(ps: PS, dashboardData: BlocksDashboardData) {
    const appStudioData = appStudioDataModel.getAppStudioData(ps)
    if (!appStudioData.dashboards) {
        appStudioData.dashboards = []
    }
    appStudioData.dashboards.push(dashboardData)
    appStudioDataModel.updateAppStudioOnMasterPage(ps, appStudioData)
}

function createDashboardDescriptor(
    ps: PS,
    dashboardPointer: DashboardPointer,
    pageRef: CompRef,
    descriptor: BlocksDashboardDescriptor
) {
    const dashboardData = createBlankDashboardData(ps, dashboardPointer, pageRef, descriptor)
    addDashboardToAppStudioModel(ps, dashboardData)
}

function removeDashboardDescriptor(ps: PS, dashboardPointer: DashboardPointer) {
    const appStudioData = appStudioDataModel.getAppStudioData(ps)
    const dashboards = appStudioData?.dashboards ?? []

    appStudioData.dashboards = dashboards.filter(
        dashboardData => dsUtils.stripHashIfExists(dashboardData.id) !== dashboardPointer.id
    )
    appStudioDataModel.updateAppStudioOnMasterPage(ps, appStudioData)
}

function updateDashboardDescriptor(
    ps: PS,
    dashboardPointer: DashboardPointer,
    descriptor: Partial<BlocksDashboardDescriptor>,
    usedRoutePaths?: string[]
) {
    const appStudioData = appStudioDataModel.getAppStudioData(ps)
    const dashboards = appStudioData?.dashboards ?? []
    const newDescriptor = {...descriptor}
    const dashboardInfo = getDashboardInfo(ps, dashboardPointer.id)

    if (descriptor.routePath && dashboardInfo.routePath !== descriptor.routePath) {
        validateRoutePathOnUniqueness(descriptor.routePath, dashboards, usedRoutePaths)

        newDescriptor.isRoutePathConnectedToTitle = false
    }

    if (descriptor.title) {
        syncDashboardTitleWithPage(ps, dashboardPointer, descriptor.title)

        if (dashboardInfo.title !== descriptor.title && dashboardInfo.isRoutePathConnectedToTitle) {
            newDescriptor.routePath = generateDashboardRoutePath(ps, descriptor.title, usedRoutePaths)
        }
    }

    appStudioData.dashboards = dashboards.map(dashboardData => {
        if (dsUtils.stripHashIfExists(dashboardData?.id) === dashboardPointer?.id) {
            return {...dashboardData, ...newDescriptor}
        }

        return dashboardData
    })

    appStudioDataModel.updateAppStudioOnMasterPage(ps, appStudioData)
}

function setDashboardIntent(ps: PS, pageRef: CompRef) {
    features.updateFeatureData(ps, pageRef, 'intent', {intent: 'dashboardPage', type: 'Intent'})
}

function createDashboardPage(ps: PS, dashboardName: string, pageRef: CompRef) {
    page.add(ps, pageRef, dashboardName, dashboardConfig.getPageStructure())
    setDashboardIntent(ps, pageRef)
    const stageContainerRef = component.getComponentToAddRef(ps, pageRef)

    const dashboardStructure = dashboardConfig.getDashboardStructure()
    const wrappedDashboardStructure = _.defaultsDeep(
        {components: [dashboardStructure]},
        dashboardConfig.appWidgetStructure
    )

    component.add(ps, stageContainerRef, pageRef, wrappedDashboardStructure)

    setInitialAppWidgetData(ps, pageRef)
}

function setPageTitle(ps: PS, pageId: string, pageTitle: string) {
    const dashboardPageData = pageData.getPageData(ps, pageId, true)
    dashboardPageData.title = pageTitle
    pageData.setPageData(ps, pageId, dashboardPageData, true)
}

function syncDashboardTitleWithPage(ps: PS, dashboardPointer: Pointer, dashboardTitle: string) {
    const pageId = appStudioDataModel.getRootCompIdByPointer(ps, dashboardPointer)
    setPageTitle(ps, pageId, dashboardTitle)
}

function duplicateDashboardPage(ps: PS, pageRef: CompRef, copyRootCompId: string) {
    page.duplicate(ps, pageRef, dsUtils.stripHashIfExists(copyRootCompId))
    setDashboardIntent(ps, pageRef)
    setInitialAppWidgetData(ps, pageRef)
}

function setInitialAppWidgetData(ps: PS, pageRef: CompRef) {
    const rootAppWidgetRef = appStudioDataModel.getRootAppWidgetByPage(ps, pageRef)

    const dashboardId = pageRef.id

    const settings = {
        dashboardId
    }

    const data = {
        controllerType: DASHBOARD_CONTROLLER_TYPE,
        settings: JSON.stringify(settings)
    }

    component.data.update(ps, rootAppWidgetRef, data, true)
}

function generateDashboardTitle(ps: PS, prefix = DASHBOARD_TITLE_DEFAULT) {
    const existingTitles = getAllDashboards(ps).map(d => d.title)

    return nameGenerator.generateName(existingTitles, {
        prefix,
        separator: ' '
    })
}

function generateDashboardRoutePath(ps: PS, newDashboardTitle: string, usedRoutePaths?: string[]) {
    const transformedDashboardTitle = newDashboardTitle.toLowerCase().replace(/\s+/g, '-')
    const existingRoutePaths = [...getAllDashboards(ps).map(d => d.routePath), ...(usedRoutePaths ?? [])]

    if (!existingRoutePaths.includes(transformedDashboardTitle)) {
        return transformedDashboardTitle
    }

    return nameGenerator.generateName(existingRoutePaths, {
        prefix: transformedDashboardTitle.replace(/\d+$/, '')
    })
}

async function createDashboard(
    ps: PS,
    dashboardPointer: DashboardPointer,
    callback?: Callback1<DashboardPointer>,
    usedRoutePaths?: string[]
) {
    const dashboardTitle = generateDashboardTitle(ps)
    const dashboardRoutePath = generateDashboardRoutePath(ps, dashboardTitle, usedRoutePaths)
    const newPageRef = page.getPageIdToAdd(ps)
    const dashboardDescriptor = {routePath: dashboardRoutePath, title: dashboardTitle}

    createDashboardDescriptor(ps, dashboardPointer, newPageRef, dashboardDescriptor)
    createDashboardPage(ps, dashboardTitle, newPageRef)

    await setInitialDashboardCode(ps, newPageRef)

    if (callback) {
        callback(dashboardPointer)
    }
}

async function duplicateDashboard(
    ps: PS,
    newDashboardPointer: DashboardPointer,
    targetDashboardPointer: DashboardPointer,
    callback?: Callback1<DashboardPointer>,
    usedRoutePaths?: string[]
) {
    const dashboardToCopy = appStudioDataModel.getData(ps, targetDashboardPointer)

    if (!dashboardToCopy) {
        throw new Error('appStudio.dashboards: provided dashboard pointer does not exists')
    }

    const dashboardTitle = generateDashboardTitle(ps, dashboardToCopy.title)
    const dashboardRoutePath = generateDashboardRoutePath(ps, dashboardTitle, usedRoutePaths)
    const newPageRef = page.getPageIdToAdd(ps)
    const descriptorToCopy = {
        ..._.omit(dashboardToCopy, ['id', 'metaData', 'type', 'rootCompId']),
        title: dashboardTitle,
        routePath: dashboardRoutePath
    } as BlocksDashboardDescriptor

    createDashboardDescriptor(ps, newDashboardPointer, newPageRef, descriptorToCopy)
    duplicateDashboardPage(ps, newPageRef, dashboardToCopy.rootCompId)
    syncDashboardTitleWithPage(ps, newDashboardPointer, dashboardTitle)

    if (callback) {
        callback(newDashboardPointer)
    }
}

// TODO consider extracting to wixCode
async function setPageCode(ps: PS, pageId: string, code: string) {
    const fileLocation = fileSystemAPI.getRoots().pages.location
    const fileName = `${fileLocation}${pageId}.js`
    const fileDescriptor = fileSystemAPI.getVirtualDescriptor(ps, fileName, false)
    await fileSystemAPI.writeFile(ps, fileDescriptor, code)
}

async function setInitialDashboardCode(ps: PS, pageRef: CompRef) {
    await setPageCode(ps, pageRef.id, dashboardInitialCode)
}

function displayDashboard(ps: PS, dashboardPointer: DashboardPointer, callback: Callback) {
    const rootCompId = appStudioDataModel.getRootCompIdByPointer(ps, dashboardPointer)
    page.navigateTo(ps, rootCompId, callback)
}

function removeDashboard(ps: PS, dashboardPointer: DashboardPointer, callback?: Callback1<PS>) {
    const rootCompId = appStudioDataModel.getRootCompIdByPointer(ps, dashboardPointer)
    removeDashboardDescriptor(ps, dashboardPointer)
    page.remove(ps, rootCompId, callback)
}

function updateDashboard(
    ps: PS,
    dashboardPointer: DashboardPointer,
    descriptor: Partial<BlocksDashboardDescriptor>,
    usedRoutePaths?: string[]
) {
    updateDashboardDescriptor(ps, dashboardPointer, descriptor, usedRoutePaths)
}

function getDashboardByRootCompId(ps: PS, rootCompId: string) {
    const dashboard = getAllDashboards(ps).find(({rootCompId: dashboardId}) => _.trim(dashboardId, '#') === rootCompId)
    return dashboard?.pointer
}

function getDashboardDataById(ps: PS, dashboardId: string) {
    return getDashboardInfo(ps, `#${dashboardId}`)
}

function validateRoutePathOnUniqueness(newRoutePath: string, dashboards, usedRoutePaths: string[]) {
    const existingRoutePaths = [...dashboards.map(item => item.routePath ?? ''), ...(usedRoutePaths ?? [])]

    if (existingRoutePaths.includes(newRoutePath)) {
        throw new Error(`updateDashboard: provided routePath is already exists - ${newRoutePath}`)
    }
}

export default {
    createDashboard,
    duplicateDashboard,
    displayDashboard,
    removeDashboard,
    updateDashboard,
    getAllDashboards,
    getDashboardByRootCompId,
    getDashboardDataById
}
